/**
 * `DeliveryProgressBar` (REDISENO-PANEL.md §2 "Barra de progreso de
 * entrega", panel-redesign-v3 T1) — shared by the list, Inicio, detail, and
 * telemetría screens (later slices); this slice only builds the primitive.
 *
 * `role="group"` + a descriptive `aria-label` (§10: "Barras de progreso con
 * role='group' y un aria-label que diga las cifras") carries the figures
 * for assistive technology — the colored segments themselves are purely
 * decorative. The error segment is floored at 1.5% width whenever
 * `errorCount > 0`, so a single error out of a very large total still
 * reads as visible red, never rounded away to ~0%.
 *
 * Inputs are defensively clamped: negative figures become 0, and
 * `delivered`/`errorCount` never exceed `total` — callers (telemetry
 * aggregates, in-flight campaigns) are not guaranteed to hand back
 * internally consistent numbers. When the 1.5% error floor would push
 * `delivered + error` past 100% (e.g. 9998/1/10000), the floor is taken out
 * of the delivered segment's *rendered width* only, never out of the error
 * segment's, so the two segments never visually overlap or overflow the
 * track. The announced (aria-label) delivered count tracks the real
 * `delivered` figure whenever the inputs are already internally consistent
 * (`delivered + errorCount <= total`) — only when they are not does the
 * announced count get normalized to `total - errorCount`, matching the
 * trimmed width (Codex finding, panel-redesign-v3 PR #380).
 */
const MIN_ERROR_PERCENT = 1.5;

function clampNonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function DeliveryProgressBar({
  delivered,
  errorCount,
  total,
  height = 6,
  deliveredAvailable = true,
  countLabel = "entregados",
  active = false,
}: {
  delivered: number;
  errorCount: number;
  total: number;
  height?: 6 | 8 | 12;
  /**
   * Codex finding 4 (panel-redesign-v3 PR #382): `delivered` is sometimes
   * only a numeric `0` fed in for this bar's own geometry when the caller's
   * 'entregado' metric is actually unavailable (an acceptable visual
   * degrade) — announcing "0 de N entregados" to assistive tech in that case
   * would read as a confirmed measurement, not a missing one. `false` hides
   * the whole group from the accessibility tree instead.
   */
  deliveredAvailable?: boolean;
  /**
   * progress-bar-sent: noun announced for the `delivered` figure. Defaults to
   * "entregados"; the campaign detail screen passes "enviados" because its bar
   * follows the sending pace (ACS-accepted), not delivery confirmations.
   */
  countLabel?: "entregados" | "enviados";
  /**
   * campaign-pace-metrics: `true` while the campaign is still sending — adds a subtle moving
   * stripe on the filled segment (CSS, disabled under `prefers-reduced-motion`). Callers pass
   * `estado === "enviando"`; Telemetría bars never pass it. Ignored for an empty or already
   * complete bar, where there is nothing left to progress.
   */
  active?: boolean;
}) {
  const safeTotal = clampNonNegative(total);
  const safeDelivered = safeTotal > 0 ? Math.min(clampNonNegative(delivered), safeTotal) : 0;
  const safeErrorCount = safeTotal > 0 ? Math.min(clampNonNegative(errorCount), safeTotal) : 0;

  const deliveredPercentRaw = safeTotal > 0 ? (safeDelivered / safeTotal) * 100 : 0;
  const errorPercentRaw = safeTotal > 0 ? (safeErrorCount / safeTotal) * 100 : 0;
  const errorPercent = safeErrorCount > 0 ? Math.max(errorPercentRaw, MIN_ERROR_PERCENT) : 0;
  // Never let the floor push the two segments' widths past 100% together —
  // the floor is "borrowed" from the delivered segment, not added on top.
  const deliveredPercent = Math.min(deliveredPercentRaw, 100 - errorPercent);
  // The announced delivered count reflects the real figures whenever they
  // are already internally consistent — the 1.5% visual floor only ever
  // trims the *rendered width*, never the announced number. Only when the
  // inputs are inconsistent (delivered + errorCount > total) is the
  // announced count normalized, to total - errorCount, so sighted and
  // assistive-tech users still read numbers that add up (Codex finding,
  // panel-redesign-v3 PR #380).
  const inputsConsistent = safeDelivered + safeErrorCount <= safeTotal;
  const announcedDelivered = inputsConsistent
    ? safeDelivered
    : Math.max(safeTotal - safeErrorCount, 0);

  // Codex finding 3 (PR #382 review round 3): when `deliveredAvailable` is
  // false but `errorCount` IS available and nonzero, hiding the WHOLE group
  // (the fix for the fabricated-delivered-value finding above) also removed
  // the only announcement of those errors for screen-reader users, while
  // sighted users still saw the red error segment. Hide the group only when
  // there is genuinely nothing useful to say (no errors either); otherwise
  // keep it in the accessibility tree and announce delivered as unavailable
  // while still stating the real total/error figures.
  const hasUnavailableDeliveredLabel = !deliveredAvailable && safeErrorCount > 0;
  const hideEntirely = !deliveredAvailable && safeErrorCount === 0;
  const animated = active && safeTotal > 0 && safeDelivered + safeErrorCount < safeTotal;
  const ariaLabel = hasUnavailableDeliveredLabel
    ? `${countLabel === "enviados" ? "Enviados" : "Entregados"} no disponible de ${safeTotal}, ${safeErrorCount} errores`
    : `${announcedDelivered} de ${safeTotal} ${countLabel}, ${safeErrorCount} errores`;

  return (
    <div
      className={`v3-progress-bar v3-progress-bar--${height}${animated ? " v3-progress-bar--active" : ""}`}
      role="group"
      aria-hidden={hideEntirely ? "true" : undefined}
      aria-label={ariaLabel}
    >
      <span className="v3-progress-bar__delivered" style={{ width: `${deliveredPercent}%` }} />
      <span className="v3-progress-bar__error" style={{ width: `${errorPercent}%` }} />
    </div>
  );
}
